// Globals
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { StyledPageWrapper, StyledTitle, StyledForm, StyledButton, StyledSelect } from './FilterFormStyles';
import { FormItem, IFormInstance } from '@/components';
import { Input, InputNumber, Radio, RadioGroup, Select } from '@/ui';
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';
import { useWatchForm } from '@/components';
import FilterMarketplaceMappingsBlock from '../FilterMarketplaceMappingsBlock/FilterMarketplaceMappingsBlock';

// Models
import { FilterType, IFilter, IFilterMarketplaceMapping } from '@/models/filters.model';
import { CategoryLocaleCodes } from '@/models/categories.model';

// Helpers
import { normalizeFilterFormValues, FilterFormFieldNames } from '../../helpers';
import { getTypeOptions, getVisibilityOptions, getCategoriesOptions } from '../../helpers/getOptions';
import { useFilterFormValidationRules } from '../../hooks/useFilterFormValidationRules';
import { useCategoriesAutocomplete } from '../../hooks';

type TFilterForm = {
  formInstance: IFormInstance;
  onSubmit: () => void;
  isEdit?: boolean;
  initialValues: IFilter | null;
};

const initialLocale = {
  [CategoryLocaleCodes.UK]: { title: '' },
  [CategoryLocaleCodes.RU]: { title: '' },
};

const FilterForm = ({ formInstance, onSubmit, isEdit, initialValues }: TFilterForm) => {
  const { t } = useTranslation(['categories']);

  const currentFilterType = useWatchForm(FilterFormFieldNames.TYPE, formInstance);
  const selectedCategoryIds =
    (useWatchForm(FilterFormFieldNames.CATEGORIES, formInstance) as string[] | undefined) ?? [];

  const rules = useFilterFormValidationRules(!!isEdit);
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const [localMappings, setLocalMappings] = useState<IFilterMarketplaceMapping[]>([]);
  const [categoryLabelById, setCategoryLabelById] = useState<Record<string, string>>({});

  useEffect(() => {
    onCategoriesSearch();
    if (isEdit && initialValues) {
      formInstance.setFieldsValue(normalizeFilterFormValues(initialValues));
    } else {
      formInstance.setFieldsValue({ locale: initialLocale });
    }

    return () => {
      onCategoriesSearch();
    };
  }, [isEdit, initialValues, formInstance]);

  useEffect(() => {
    setCategoryLabelById((prev) => {
      const next = { ...prev };

      (initialValues?.categories ?? []).forEach((category) => {
        if (category?.id && category?.name) {
          next[category.id] = category.name;
        }
      });

      (categoriesList ?? []).forEach((category) => {
        if (category?.id && category?.name) {
          next[category.id] = category.name;
        }
      });

      return next;
    });
  }, [categoriesList, initialValues?.categories]);

  useEffect(() => {
    if (!isEdit || !initialValues?.id) {
      setLocalMappings([]);

      return;
    }

    if (initialValues.marketplace_mappings !== undefined) {
      setLocalMappings(initialValues.marketplace_mappings);
    }
  }, [isEdit, initialValues?.id, initialValues?.marketplace_mappings]);

  const categoryOptions = useMemo(() => {
    const selectedCategories = selectedCategoryIds
      .filter((id) => categoryLabelById[id])
      .map((id) => ({ id, name: categoryLabelById[id] }));

    return getCategoriesOptions(categoriesList ?? [], [...(initialValues?.categories ?? []), ...selectedCategories]);
  }, [categoriesList, categoryLabelById, initialValues?.categories, selectedCategoryIds]);

  return (
    <StyledPageWrapper>
      <StyledForm onFinish={onSubmit} form={formInstance}>
        <StyledTitle>{isEdit ? t('edit_filter_form_title') : t('create_filter_form_title')}</StyledTitle>

        <FormItem name={LOCALES_FIELD_NAME}>
          <LocalesFormFields labelPrefix={'filter_form_'} />
        </FormItem>

        <FormItem
          name={FilterFormFieldNames.POSITION}
          label={t('filter_form_position_label')}
          rules={rules[FilterFormFieldNames.POSITION]}
        >
          <InputNumber />
        </FormItem>

        <FormItem
          name={FilterFormFieldNames.TYPE}
          label={t('filter_form_type_label')}
          rules={rules[FilterFormFieldNames.TYPE]}
        >
          <Select options={getTypeOptions(t)} />
        </FormItem>

        {currentFilterType === FilterType.SLIDER && null}

        <FormItem name={FilterFormFieldNames.CATEGORIES} label={t('filter_form_categories_label')}>
          <StyledSelect
            allowClear
            showSearch
            options={categoryOptions}
            onSearch={onCategoriesSearch}
            isMultiselect
            maxTagCount={100}
          />
        </FormItem>

        <FormItem
          name={FilterFormFieldNames.STATUS}
          label={t('filter_visibility_column_title')}
          rules={rules[FilterFormFieldNames.STATUS]}
        >
          <Select options={getVisibilityOptions(t)} />
        </FormItem>

        <FormItem
          label={t('filter_shown')}
          name={FilterFormFieldNames.IS_SHOWN}
          rules={rules[FilterFormFieldNames.IS_SHOWN]}
        >
          <RadioGroup>
            <Radio value={true}>{t('shown')}</Radio>
            <Radio value={false}>{t('filter_non_visible')}</Radio>
          </RadioGroup>
        </FormItem>

        <FormItem
          label={t('filter_form_required_label')}
          name={FilterFormFieldNames.IS_REQUIRED}
          rules={rules[FilterFormFieldNames.IS_REQUIRED]}
        >
          <RadioGroup>
            <Radio value={true}>{t('filter_yes_title')}</Radio>
            <Radio value={false}>{t('filter_non_title')}</Radio>
          </RadioGroup>
        </FormItem>

        {isEdit && (
          <FormItem
            name={FilterFormFieldNames.SLUG}
            label={t('filter_form_slug_label')}
            rules={rules[FilterFormFieldNames.SLUG]}
          >
            <Input />
          </FormItem>
        )}

        {isEdit && initialValues?.id && (
          <FilterMarketplaceMappingsBlock
            filterId={initialValues.id}
            mappings={localMappings}
            onMappingsChange={setLocalMappings}
          />
        )}

        <StyledButton type="primary" htmlType="submit">
          {t('save_filter')}
        </StyledButton>
      </StyledForm>
    </StyledPageWrapper>
  );
};

export default FilterForm;
